import { useGlobalState } from '@/hooks';
import { getShipments } from '@/modules/pmt/fetchers';
import { gtmInvalidParcelSearches } from '@/utils';
import { useCallback, useEffect, useState } from 'react';
import { getGaClientId, getSessionId } from '../utils/ga4.utils';

interface FetchShipmentsOptions {
  carrierSlug?: string;
}

interface FetchArgs {
  parcelIds: string[];
  carrierSlug?: string;
}

const useFetchShipments = (onComplete?: (successCount: number) => void) => {
  const { pmtCurrentLanguageCode, firestoreUuid, isLoggedIn, isMultiLanguagePage } = useGlobalState();
  const [fetchArgs, setFetchArgs] = useState<FetchArgs>({ parcelIds: [] });

  useEffect(() => {
    if (!fetchArgs.parcelIds.length || !firestoreUuid) return;
    // TODO: FE has to use Promise.all() to call search shipments concurrently,
    // but BE should support plural parcelIds
    (async () => {
      const searchPromises = fetchArgs.parcelIds.map(async (parcelId) => {
        return getShipments({
          uuid: firestoreUuid,
          parcelId,
          language: pmtCurrentLanguageCode,
          isLoggedIn,
          gaClientId: getGaClientId(),
          gaSessionId: getSessionId(isMultiLanguagePage),
          eventTimestamp: Date.now().toString(),
          carrierSlug: fetchArgs.carrierSlug,
        });
      });

      try {
        const statuses = await Promise.all(searchPromises);
        let successCount = 0;

        statuses.forEach((result) => {
          if (result?.status === 400) {
            gtmInvalidParcelSearches();
          } else if (result?.ok) {
            successCount++;
          }
        });

        if (onComplete) {
          onComplete(successCount);
        }
      } catch (err) {
        console.error(err);
      }
    })();
    setFetchArgs({ parcelIds: [] });
  }, [firestoreUuid, fetchArgs, pmtCurrentLanguageCode, isLoggedIn, isMultiLanguagePage, onComplete]);

  const fetchShipments = useCallback((parcelIds: string[], options?: FetchShipmentsOptions) => {
    setFetchArgs({ parcelIds, carrierSlug: options?.carrierSlug });
  }, []);

  return { fetchShipments };
};

export { useFetchShipments };
